import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ReturnTrackingManagementPanel = () => {
    const { token } = useAuth();
    const [serviceStatus, setServiceStatus] = useState(null);
    const [activeTracking, setActiveTracking] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedReturnId, setSelectedReturnId] = useState('');
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        loadServiceStatus();
        loadActiveTracking();
        loadStatistics();
        loadSettings();
    }, []);

    const loadServiceStatus = async () => {
        try {
            const response = await api.get('/api/return-tracking/status', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setServiceStatus(response.data.data);
        } catch (error) {
            console.error('Servis durumu yüklenirken hata:', error);
        }
    };

    const loadActiveTracking = async () => {
        try {
            const response = await api.get('/api/return-tracking/active-tracking', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setActiveTracking(response.data.data.activePolls || []);
        } catch (error) {
            console.error('Aktif takipler yüklenirken hata:', error);
        }
    };

    const loadStatistics = async () => {
        try {
            const response = await api.get('/api/return-tracking/statistics', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStatistics(response.data.data);
        } catch (error) {
            console.error('İstatistikler yüklenirken hata:', error);
        }
    };

    const loadSettings = async () => {
        try {
            const response = await api.get('/api/return-tracking/settings', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(response.data.data);
        } catch (error) {
            console.error('Ayarlar yüklenirken hata:', error);
        }
    };

    const handleStartService = async () => {
        setLoading(true);
        try {
            await api.post('/api/return-tracking/start', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('İade takip servisi başlatıldı');
            loadServiceStatus();
        } catch (error) {
            toast.error('Servis başlatılamadı');
        } finally {
            setLoading(false);
        }
    };

    const handleStopService = async () => {
        setLoading(true);
        try {
            await api.post('/api/return-tracking/stop', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('İade takip servisi durduruldu');
            loadServiceStatus();
        } catch (error) {
            toast.error('Servis durdurulamadı');
        } finally {
            setLoading(false);
        }
    };

    const handleStartTracking = async (returnId) => {
        try {
            await api.post(`/api/return-tracking/start-tracking/${returnId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('İade takibi başlatıldı');
            loadActiveTracking();
        } catch (error) {
            toast.error('İade takibi başlatılamadı');
        }
    };

    const handleStopTracking = async (returnId) => {
        try {
            await api.post(`/api/return-tracking/stop-tracking/${returnId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('İade takibi durduruldu');
            loadActiveTracking();
        } catch (error) {
            toast.error('İade takibi durdurulamadı');
        }
    };

    const handleTestTracking = async () => {
        if (!selectedReturnId) {
            toast.warn('Lütfen test edilecek iade ID\'sini girin');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post(`/api/return-tracking/test/${selectedReturnId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTestResult(response.data.data);
            toast.success('Test tamamlandı');
        } catch (error) {
            toast.error('Test başarısız');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSettings = async (updates) => {
        try {
            await api.put('/api/return-tracking/settings', updates, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Ayarlar güncellendi');
            loadSettings();
        } catch (error) {
            toast.error('Ayarlar güncellenemedi');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'running': return 'text-green-600';
            case 'stopped': return 'text-red-600';
            default: return 'text-gray-600';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'running': return '🟢';
            case 'stopped': return '🔴';
            default: return '⚪';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
                🚚 İade Takip Yönetimi
            </h2>

            {/* Servis Durumu */}
            <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-700">Servis Durumu</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                    {serviceStatus ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center">
                                <div className={`text-2xl ${getStatusColor(serviceStatus.isRunning ? 'running' : 'stopped')}`}>
                                    {getStatusIcon(serviceStatus.isRunning ? 'running' : 'stopped')}
                                </div>
                                <div className="text-sm text-gray-600">
                                    {serviceStatus.isRunning ? 'Çalışıyor' : 'Durduruldu'}
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    {serviceStatus.activePolls || 0}
                                </div>
                                <div className="text-sm text-gray-600">Aktif Takip</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    {serviceStatus.stats?.successRate?.toFixed(1) || 0}%
                                </div>
                                <div className="text-sm text-gray-600">Başarı Oranı</div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-gray-500">Yükleniyor...</div>
                    )}
                    
                    <div className="mt-4 flex gap-2">
                        <button
                            onClick={handleStartService}
                            disabled={loading || serviceStatus?.isRunning}
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                            Servisi Başlat
                        </button>
                        <button
                            onClick={handleStopService}
                            disabled={loading || !serviceStatus?.isRunning}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            Servisi Durdur
                        </button>
                        <button
                            onClick={() => {
                                loadServiceStatus();
                                loadActiveTracking();
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Yenile
                        </button>
                    </div>
                </div>
            </div>

            {/* Aktif Takip Listesi */}
            <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-700">Aktif İade Takipleri</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                    {activeTracking.length > 0 ? (
                        <div className="space-y-2">
                            {activeTracking.map((tracking) => (
                                <div key={tracking.returnId} className="flex items-center justify-between bg-white p-3 rounded border">
                                    <div>
                                        <div className="font-medium">İade ID: {tracking.returnId}</div>
                                        <div className="text-sm text-gray-600">
                                            Durum: {tracking.status} | 
                                            Hata: {tracking.errorCount} | 
                                            Başlangıç: {new Date(tracking.startTime).toLocaleString()}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleStopTracking(tracking.returnId)}
                                        className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                                    >
                                        Durdur
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-gray-500">Aktif takip bulunmuyor</div>
                    )}
                </div>
            </div>

            {/* İstatistikler */}
            {statistics && (
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">İade İstatistikleri (Son 30 Gün)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">{statistics.returns?.total || 0}</div>
                            <div className="text-sm text-gray-600">Toplam İade</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">{statistics.returns?.autoTrackingEnabled || 0}</div>
                            <div className="text-sm text-gray-600">Otomatik Takip</div>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-lg">
                            <div className="text-2xl font-bold text-yellow-600">{statistics.returns?.autoTrackingDisabled || 0}</div>
                            <div className="text-sm text-gray-600">Manuel Takip</div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">{statistics.service?.activePolls || 0}</div>
                            <div className="text-sm text-gray-600">Aktif Takip</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Test Bölümü */}
            <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-700">İade Takip Testi</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex gap-2 mb-4">
                        <input
                            type="text"
                            placeholder="İade ID girin"
                            value={selectedReturnId}
                            onChange={(e) => setSelectedReturnId(e.target.value)}
                            className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={handleTestTracking}
                            disabled={loading || !selectedReturnId}
                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                        >
                            Test Et
                        </button>
                    </div>
                    
                    {testResult && (
                        <div className="bg-white p-4 rounded border">
                            <h4 className="font-semibold mb-2">Test Sonucu:</h4>
                            <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto">
                                {JSON.stringify(testResult, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            </div>

            {/* Ayarlar */}
            {settings && (
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Takip Ayarları</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Otomatik Takip</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.auto_tracking_enabled}
                                        onChange={(e) => handleUpdateSettings({ auto_tracking_enabled: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Email Bildirimleri</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.email_notifications}
                                        onChange={(e) => handleUpdateSettings({ email_notifications: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Push Bildirimleri</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.push_notifications}
                                        onChange={(e) => handleUpdateSettings({ push_notifications: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReturnTrackingManagementPanel;
